import { useState, useEffect } from "react";
import { Table } from "react-bootstrap";
import { useParams } from "react-router-dom";
import BackButton from "../../TeacherAdmin/Goback/GoBack";
import RefreshButtonTable from "../../../common/RefreshButtonTable";
import TableError from "../../common/TableError";
import TableSkeleton from "../../common/TableSkeleton";
import TablePagination from "../../TeacherAdmin/pagination/TablePagination";
import { useGetImportLogRowDetails } from "../../../../hooks/import/school/useGetImportLogRowDetails";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import "../../../../common/styles/table.css";

/**
 * Row status badge
 */
const ROW_STATUS_BADGE = {
  success: { bg: "#ecfdf3", color: "#027a48", label: "Success" },
  failed: { bg: "#fef3f2", color: "#b42318", label: "Failed" },
};

/**
 * ImportLogDetail - Displays the row-level details of a specific import log
 * Shows summary stats + table of each imported row with status/message
 */
const ImportLogDetail = () => {
  const { logId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(""); // "" = All, "failed", "success"

  const { rows, meta, loading, error, getImportLogRows } =
    useGetImportLogRowDetails();

  useEffect(() => {
    if (logId) {
      getImportLogRows(logId, {
        page: currentPage,
        per_page: ITEMS_PER_PAGE,
        status: statusFilter || undefined,
      });
    }
  }, [logId, currentPage, statusFilter]);

  const handleRefresh = () => {
    if (logId) {
      getImportLogRows(logId, {
        page: currentPage,
        per_page: ITEMS_PER_PAGE,
        status: statusFilter || undefined,
      });
    }
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const totalPages = meta?.last_page || 1;
  const totalRows = meta?.total || 0;
  const successCount = rows
    ? rows.filter((r) => r.status === "success").length
    : 0;
  const failedCount = rows
    ? rows.filter((r) => r.status === "failed").length
    : 0;

  // Show skeleton on initial load
  if (loading && rows === null) {
    return (
      <div>
        <div className="pt-2">
          <BackButton />
        </div>
        <div className="pb-3 fs-3 fw-semibold">Import Detail #{logId}</div>
        <TableSkeleton rows={ITEMS_PER_PAGE} columns={4} />
      </div>
    );
  }

  return (
    <div>
      <div className="pt-2">
        <BackButton />
      </div>
      <div className="pb-3 fs-3 fw-semibold">Import Detail #{logId}</div>

      {/* Summary stats */}
      <div className="d-flex gap-3 mb-3 flex-wrap">
        <div
          className="d-flex align-items-center gap-2 px-3 py-2"
          style={{
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            border: "1px solid #e4e7ec",
          }}
        >
          <span style={{ color: "#667085", fontSize: "14px" }}>
            Total Rows:
          </span>
          <span style={{ fontWeight: 600, fontSize: "14px" }}>{totalRows}</span>
        </div>
        <div
          className="d-flex align-items-center gap-2 px-3 py-2"
          style={{
            backgroundColor: "#ecfdf3",
            borderRadius: "8px",
            border: "1px solid #a6f4c5",
          }}
        >
          <span style={{ color: "#027a48", fontSize: "14px" }}>Success:</span>
          <span style={{ fontWeight: 600, fontSize: "14px", color: "#027a48" }}>
            {successCount}
          </span>
        </div>
        <div
          className="d-flex align-items-center gap-2 px-3 py-2"
          style={{
            backgroundColor: "#fef3f2",
            borderRadius: "8px",
            border: "1px solid #fecdca",
          }}
        >
          <span style={{ color: "#b42318", fontSize: "14px" }}>Failed:</span>
          <span style={{ fontWeight: 600, fontSize: "14px", color: "#b42318" }}>
            {failedCount}
          </span>
        </div>
      </div>

      <div className="pt-2 d-flex justify-content-between align-items-center gap-2 mb-2">
        <select
          className="form-select form-select-sm"
          style={{ width: "140px" }}
          value={statusFilter}
          onChange={handleStatusFilterChange}
          disabled={loading}
        >
          <option value="">All</option>
          <option value="failed">Failed</option>
          <option value="success">Success</option>
        </select>
        <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
      </div>

      {error ? (
        <TableError
          error={error}
          handleRefresh={handleRefresh}
          loading={loading}
        />
      ) : !rows || rows.length === 0 ? (
        <div className="alert alert-info mt-3" role="alert">
          No row details found for this import.
        </div>
      ) : (
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>Row #</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const badge =
                    ROW_STATUS_BADGE[row.status] || ROW_STATUS_BADGE.failed;
                  return (
                    <tr key={row.id}>
                      <td className="custom-id">{row.row_number}</td>
                      <td>{row.data?.email || "-"}</td>
                      <td>
                        <span
                          style={{
                            backgroundColor: badge.bg,
                            color: badge.color,
                            padding: "4px 12px",
                            borderRadius: "16px",
                            fontSize: "13px",
                            fontWeight: 500,
                          }}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td
                        style={{
                          color:
                            row.status === "failed" ? "#b42318" : "#344054",
                          fontSize: "13px",
                        }}
                      >
                        {row.message || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};

export default ImportLogDetail;
